import React, { useState } from 'react';
import { Typography, Image } from 'lib/components';
import classes from './index.module.scss';
import { Checkbox } from 'lib/components/FormFields';
import Scrollbars from 'react-custom-scrollbars';
import { useWindowResize } from 'hooks/useWindowResize';

/**
 * Column configuration for the log table header
 * Defines the sortable columns in the table
 */
const toolbarItems = [
	{ id: '0', label: 'Requested by' },
	{ id: '1', label: 'Ticket ID' },
	{ id: '2', label: 'Sent to' },
	{ id: '3', label: 'Amount' },
	{ id: '4', label: 'Price' },
	{ id: '5', label: 'Created Date' },
];

/**
 * Sample log data for demonstration
 * Contains entries related to job requests and assignments
 * Each entry has an ID, checked state, and display information
 */
const tableItems = [
	{
		id: '0',
		checked: true,
		requestedBy: 'Spencer Memorial Hospital',
		ticketId: '0001478',
		sentTo: 'Regina Smith',
		amount: 1,
		price: 14.99,
		createdDate: '01.01.21 - 09:45AM',
	},
	{
		id: '1',
		checked: false,
		requestedBy: 'Spencer Memorial Hospital',
		ticketId: '0001474',
		sentTo: 'Johnina Doetson',
		amount: 15,
		price: 259.49,
		createdDate: '01.01.21 - 09:10AM',
	},
	{
		id: '2',
		checked: false,
		requestedBy: 'Spencer Memorial Hospital',
		ticketId: '0001471',
		sentTo: 'Inventory LLC',
		amount: 100,
		price: 1000.99,
		createdDate: '01.01.21 - 07:25AM',
	},
];

/**
 * Pagination configuration
 * Defines available pages for navigation
 */
const pages = [1, 2, 3, 4];

/**
 * Log component
 * Displays a table of job logs with filtering and pagination
 * Allows selecting and performing actions on multiple log entries
 *
 * @returns {JSX.Element} - Rendered component
 */
const Log = () => {
	// State for "select all" checkbox
	const [allChecked, setAllChecked] = useState(false);
	// State for current pagination page
	const [selectedPage, setSelectedPage] = useState(1);
	// Responsive design helpers
	const { isSmallDevice, isSmallerDevice } = useWindowResize();
	return (
		<div
			className={classes['dispatches-logs-container']}
			style={{
				padding: isSmallDevice ? '0 1rem' : undefined,
			}}
		>
			{/* Action buttons toolbar */}
			<div className={classes['row']} style={{ justifyContent: 'space-between' }}>
				{/* Left side action buttons */}
				<div className={classes['row']}>
					{/* Accept All dropdown button */}
					<button
						className={classes['button']}
						style={{
							width: '8rem',
							backgroundColor: '#E4F4F6',
							borderColor: '#A6E5E9',
							padding: '0 1rem',
							marginTop: '1rem',
							marginRight: '1rem',
						}}
					>
						<Typography variant={isSmallerDevice ? 'h5' : 'h6'} weight="light">
							Accept All
						</Typography>
						<Image
							src="icArrowDown"
							style={{
								marginLeft: 'auto',
							}}
						/>
					</button>

					{/* Archive button */}
					<button
						className={classes['button']}
						style={{
							backgroundColor: '#F2F2F2',
							borderColor: '#A9A9A9',
							marginTop: '1rem',
						}}
					>
						<Image
							src="icArchive"
							style={{
								marginRight: '0.5rem',
							}}
						/>
						<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Archive</Typography>
					</button>
				</div>

				{/* Filter button */}
				<button
					className={classes['button']}
					style={{
						backgroundColor: '#E7F7F9',
						marginTop: '1rem',
					}}
				>
					<Image
						src="icFilterPrimary"
						style={{
							marginRight: '0.5rem',
						}}
					/>
					<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Filter by</Typography>
				</button>
			</div>

			{/* Table container */}
			<div className={classes['table-wrapper']}>
				<div className={classes['table-container']}>
					{/* Table header with sortable columns */}
					<div className={classes['toolbar']}>
						{/* Select all checkbox */}
						<Checkbox
							checked={allChecked}
							onChange={() => setAllChecked(!allChecked)}
							style={{
								margin: '0 2rem 0.1rem 2rem',
							}}
						/>
						{/* Column headers with sort indicators */}
						{toolbarItems.map(({ id, label }) => (
							<div className={classes['col']} key={id}>
								<Typography variant={isSmallerDevice ? 'h5' : 'h6'} weight="light">
									{label}
								</Typography>
								<Image
									src="icArrowDown"
									style={{
										marginLeft: '0.5rem',
									}}
								/>
							</div>
						))}
					</div>

					{/* Table body with scrollable content */}
					<div
						className={classes['container']}
						style={{
							padding: '0 1rem',
						}}
					>
						<Scrollbars>
							{/* Map through log items and render each row */}
							{tableItems.map(
								({ id, amount, checked, createdDate, price, requestedBy, sentTo, ticketId }) => (
									<div key={id} className={classes['table-row']}>
										{/* Row selection checkbox */}
										<Checkbox
											checked={checked}
											style={{
												margin: '0 2rem',
											}}
										/>
										{/* Requested by column */}
										<Typography variant={'h6'} className={classes['col']}>
											{requestedBy}
										</Typography>
										{/* Ticket ID column */}
										<Typography variant={'h6'} className={classes['col']}>
											{ticketId}
										</Typography>
										{/* Sent to column */}
										<Typography variant={'h6'} className={classes['col']}>
											{sentTo}
										</Typography>
										{/* Amount column */}
										<Typography variant={'h6'} className={classes['col']}>
											{amount}
										</Typography>
										{/* Price column */}
										<Typography variant={'h6'} className={classes['col']}>
											{price}$
										</Typography>
										{/* Created date column */}
										<Typography variant={'h6'} className={classes['col']}>
											{createdDate}
										</Typography>
									</div>
								)
							)}
						</Scrollbars>
					</div>
				</div>
			</div>
			{/* Pagination controls */}
			<div className={classes['bottom-toolbar']}>
				{/* First page button */}
				<button className={classes['badge']}>
					<Typography variant={isSmallerDevice ? 'h5' : 'h6'} color="darkgrey">
						First
					</Typography>
				</button>

				{/* Previous page button */}
				<button className={classes['badge']}>
					<Typography variant={isSmallerDevice ? 'h5' : 'h6'} color="darkgrey">
						Prev.
					</Typography>
				</button>

				{/* Page number buttons */}
				{pages.map((page) => {
					// Apply active styling to the currently selected page
					const classname =
						page === selectedPage ? `${classes['badge']} ${classes['active']}` : `${classes['badge']}`;
					return (
						<button className={classname} key={page.toString()} onClick={() => setSelectedPage(page)}>
							<Typography
								variant={isSmallerDevice ? 'h5' : 'h6'}
								color={page === selectedPage ? 'primary' : 'darkgrey'}
							>
								{page}
							</Typography>
						</button>
					);
				})}

				{/* Next page button */}
				<button className={classes['badge']}>
					<Typography variant={isSmallerDevice ? 'h5' : 'h6'} color="darkgrey">
						Next
					</Typography>
				</button>

				{/* Last page button */}
				<button className={classes['badge']}>
					<Typography variant={isSmallerDevice ? 'h5' : 'h6'} color="darkgrey">
						Last
					</Typography>
				</button>
			</div>
		</div>
	);
};

export default Log;
